import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Navigate, Route, Routes, useLocation } from 'react-router-dom';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import Home from './components/Home';
import Login from './components/Login';
import Dashboard from './components/Dashboard';
import AccountInfo from './components/AccountInfo';
import ChangePassword from './components/ChangePassword';
import ForgotPassword from './components/ForgotPassword';
import ResetPassword from './components/ResetPassword';
import PrivacyConsent from './components/PrivacyConsent';
import ProspectApply from './components/ProspectApply';
import API_ENDPOINTS from './config/apiConfig';

const theme = createTheme({
  palette: {
    primary: {
      main: '#1B4965', // Deep ocean blue
      light: '#62B6CB', // Light ocean blue
      dark: '#0C2D48', // Dark ocean blue
    },
    secondary: {
      main: '#FF715B', // Coral accent
      light: '#FF9B8B', // Light coral
      dark: '#CC5B49', // Dark coral
    },
    background: {
      default: '#F8F9FA',
      paper: '#FFFFFF',
    },
    text: {
      primary: '#1B4965',
      secondary: '#5C6B73',
    },
  },
  typography: {
    fontFamily: "'Poppins', 'Roboto', sans-serif",
    h4: {
      fontWeight: 600,
      color: '#1B4965',
    },
    h5: {
      fontWeight: 500,
      color: '#1B4965',
    },
    h6: {
      fontWeight: 500,
      color: '#1B4965',
    },
  },
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          textTransform: 'none',
          fontWeight: 500,
          padding: '10px 24px',
        },
        contained: {
          boxShadow: 'none',
          '&:hover': {
            boxShadow: '0 6px 12px rgba(27, 73, 101, 0.12)',
          },
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 12,
          boxShadow: '0 4px 12px rgba(27, 73, 101, 0.08)',
          transition: 'box-shadow 0.2s',
          '&:hover': {
            boxShadow: '0 8px 16px rgba(27, 73, 101, 0.12)',
          },
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          borderRadius: 12,
          boxShadow: '0 4px 12px rgba(27, 73, 101, 0.08)',
        },
      },
    },
    MuiTab: {
      styleOverrides: {
        root: {
          textTransform: 'none',
          fontWeight: 500,
          fontSize: '1rem',
          '@media (max-width:599.95px)': {
            fontSize: '0.875rem',
            minWidth: 90,
            paddingLeft: 12,
            paddingRight: 12,
          },
        },
      },
    },
    MuiTableContainer: {
      styleOverrides: {
        root: { overflowX: 'auto' },
      },
    },
    MuiDialog: {
      styleOverrides: {
        paper: {
          '@media (max-width:599.95px)': {
            margin: 12,
            width: 'calc(100% - 24px)',
            maxHeight: 'calc(100% - 24px)',
          },
        },
      },
    },
    MuiDialogActions: {
      styleOverrides: {
        root: { flexWrap: 'wrap', gap: 8 },
      },
    },
    MuiIconButton: {
      styleOverrides: {
        root: {
          '@media (pointer:coarse)': { minWidth: 40, minHeight: 40 },
        },
      },
    },
  },
});

theme.typography.h4 = {
  ...theme.typography.h4,
  [theme.breakpoints.down('sm')]: { fontSize: '1.5rem' },
};
theme.typography.h5 = {
  ...theme.typography.h5,
  [theme.breakpoints.down('sm')]: { fontSize: '1.25rem' },
};

function App() {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [loading, setLoading] = useState(true);
  const [userRole, setUserRole] = useState('');
  const [agreementSigned, setAgreementSigned] = useState(false);

  useEffect(() => {
    const checkAuth = async () => {
      try {
        const response = await fetch(API_ENDPOINTS.CHECK_AUTH, {
          method: 'GET',
          credentials: 'include',
        });
        const data = await response.json();
        //console.log('Auth Check:', data);
        setIsAuthenticated(data.isAuthenticated);
        setUserRole(data.user.role);
        setAgreementSigned(data.user.agreement_signed || false);
      } catch (error) {
        console.error('Error checking authentication:', error);
      } finally {
        setLoading(false);
      }
    };

    checkAuth();
  }, []);

  if (loading) return <div>Loading...</div>;

  return (
    <ThemeProvider theme={theme}>
      <Router>
        <Routes>
          <Route path="/" element={<Login setIsAuthenticated={setIsAuthenticated} isAuthenticated={isAuthenticated} setUserRole={setUserRole} setAgreementSigned={setAgreementSigned} />} />
          <Route path="/login" element={<Login setIsAuthenticated={setIsAuthenticated} isAuthenticated={isAuthenticated} setUserRole={setUserRole} setAgreementSigned={setAgreementSigned} />} />
          <Route path="/privacy-consent" element={
            <PrivateRoute isAuthenticated={isAuthenticated} agreementSigned={true} skipAgreementCheck>
              <PrivacyConsent onAccepted={() => setAgreementSigned(true)} />
            </PrivateRoute>
          } />
          <Route path="/dashboard" element={
            <PrivateRoute isAuthenticated={isAuthenticated} agreementSigned={agreementSigned}>
              <Dashboard isAuthenticated={isAuthenticated} role={userRole} />
            </PrivateRoute>
          } />
          <Route path="/change-password" element={
            <PrivateRoute isAuthenticated={isAuthenticated} agreementSigned={true} skipAgreementCheck>
              <ChangePassword />
            </PrivateRoute>
          } />
          <Route path="/account-info" element={
            <PrivateRoute isAuthenticated={isAuthenticated} agreementSigned={agreementSigned}>
              <AccountInfo />
            </PrivateRoute>
          } />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/reset-password" element={<ResetPassword />} />
          <Route path="/apply/:token" element={<ProspectApply />} />
        </Routes>
      </Router>
    </ThemeProvider>
  );
}

function PrivateRoute({ children, isAuthenticated, agreementSigned, skipAgreementCheck }) {
  //console.log('PrivateRoute isAuthenticated:', isAuthenticated);
  if (!isAuthenticated) return <Navigate to="/login" replace />;
  if (!skipAgreementCheck && !agreementSigned) return <Navigate to="/privacy-consent" replace />;
  return children;
}

export default App;
